/** @jsx jsx */
import { Box, Button, Flex, Grid, Inline, SingleSelect } from "@eworkit/iris";
import { useBreakpointIndex } from "@theme-ui/match-media";
import { FC, KeyboardEvent, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { Icon } from "@/components/Icon";
import { ILocation } from "@/data/locations";
import { KeywordFilter, LocationFilter } from "@/views/components/Filters";

interface JobRequestsMainFiltersProps {
  queryValue?: string;
  onQueryChange?: (query?: string) => void;

  locationValue?: ILocation;
  onLocationChange?: (location?: ILocation) => void;

  radiusValue?: number;
  onRadiusChange?: (radius?: number) => void;

  onSearchButtonClick?: () => void;
}

export const JobRequestsMainFilters: FC<JobRequestsMainFiltersProps> = ({
  queryValue,
  onQueryChange,
  locationValue,
  onLocationChange,
  radiusValue,
  onRadiusChange,
  onSearchButtonClick,
}) => {
  const { t } = useTranslation("public");

  const breakpoint = useBreakpointIndex();

  const radiusOptions = useMemo(() => {
    const options = [0, 5, 10, 20, 30, 50, 100, 150];

    if (radiusValue !== undefined && !options.includes(radiusValue / 1000)) {
      options.push(radiusValue / 1000);
    }

    return options.map(n => ({
      value: n,
      label: t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.PLUS_KM", { value: n }),
    }));
  }, [radiusValue]);

  const handleLocationChange = useCallback(
    (location?: ILocation) => {
      onRadiusChange?.(location?.city ? 20000 : 0);
      onLocationChange?.(location);
    },
    [onRadiusChange, onLocationChange],
  );

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (event.key === "Enter") {
      if (onSearchButtonClick) {
        onSearchButtonClick();
      }
    }
  }, []);

  return (
    <Box
      backgroundColor={[null, null, null, "n0"]}
      borderRadius="lg"
      sx={{
        borderWidth: [null, null, null, 1],
        borderStyle: "solid",
        borderColor: "n200",
      }}>
      <Grid columns={["1fr", null, null, "5fr 5fr 2fr"]} gap={["xs", null, null, "0"]}>
        <Flex
          pl={[null, null, null, "sm"]}
          pr={[null, null, null, "xs"]}
          flexDirection="column"
          justifyContent="center">
          <KeywordFilter
            sx={{
              "& > .el-form-select-next__control, & > .el-form-text": {
                border: [null, null, null, "none !important"],
                "&:hover": {
                  boxShadow: [null, null, null, "none !important"],
                },
              },
            }}
            size="lg"
            name="query"
            value={queryValue}
            onChange={v => onQueryChange?.(v.query)}
            placeholder={t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.SEARCH_JOBS_BY_KEYWORD")}
            noOptionsMessage={() => null}
            onKeyDown={e => handleKeyPress(e)}
          />
        </Flex>

        <Flex
          pl={[null, null, null, "xs"]}
          flexDirection="column"
          justifyContent="center"
          sx={{
            borderLeftWidth: [null, null, null, 1],
            borderStyle: "solid",
            borderColor: "n200",
          }}>
          <Inline gap="xs" alignItems="center">
            <LocationFilter
              sx={{
                "& > .el-form-select-next__control, & > .el-form-text": {
                  border: [null, null, null, "none !important"],
                  "&:hover": {
                    boxShadow: [null, null, null, "none !important"],
                  },
                },
              }}
              name="location"
              value={locationValue}
              size="lg"
              onChange={v => handleLocationChange(v.location)}
              placeholder={t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.SELECT_LOCATION")}
              icons={{
                clear: <Icon icon="close" size="md" color="grey2" />,
              }}
              components={{ DropdownIndicator: () => null }}
              onKeyDown={e => handleKeyPress(e)}
            />

            {breakpoint >= 3 && locationValue?.city && (
              <Box
                sx={{
                  borderLeftWidth: [null, null, null, 1],
                  borderStyle: "solid",
                  borderColor: "n200",
                }}>
                <SingleSelect
                  sx={{
                    borderColor: "white !important",
                  }}
                  size="lg"
                  clearable={false}
                  options={radiusOptions}
                  value={radiusOptions.find(o => o.value * 1000 === radiusValue)}
                  onChange={v => onRadiusChange?.(v ? v.value * 1000 : undefined)}
                />
              </Box>
            )}
          </Inline>
        </Flex>

        <Flex p={[null, null, null, "xs"]} pt="xs" flexDirection="column">
          <Button type="submit" size={["md", null, null, "lg"]} variant="primary" onClick={onSearchButtonClick}>
            {t("LANDING_PAGE.JOB_REQUEST.LIST.FILTERS.BUTTON_SEARCH")}
          </Button>
        </Flex>
      </Grid>
    </Box>
  );
};
